import { ApiService } from "@/services/api/ApiService";
import type { DemoGenieToken, DemoTokenResolution } from "@/types/demo-genie-token";

/**
 * Private, token-gated demo route (/demo/:token) — Demo Forge prospect demos.
 * Mint/revoke/list are restricted server-side to demo-owner accounts; resolve
 * is public (the token itself is the access grant).
 */
export class DemoGenieTokenService {
  static async resolve(token: string): Promise<DemoTokenResolution> {
    try {
      const response = await ApiService.invoke<{
        status: "ok" | "expired";
        agent?: any;
        companyName?: string | null;
        expiresAt?: string;
      }>(
        { resource: "genies", action: "demo-token", data: { token } },
        "public",
      );

      if (!response) return { status: "not_found" };
      if (response.status === "ok") {
        return {
          status: "ok",
          agent: response.agent,
          companyName: response.companyName || "",
          expiresAt: response.expiresAt || "",
        };
      }
      return { status: "expired", companyName: response.companyName ?? null };
    } catch {
      return { status: "not_found" };
    }
  }

  static async mint(params: {
    agentId: string;
    companyName: string;
    expiresInDays?: number;
  }): Promise<DemoGenieToken> {
    const response = await ApiService.invoke<{ demoToken: DemoGenieToken }>({
      resource: "demo-genie-tokens",
      action: "create",
      data: params,
    });
    return response.demoToken;
  }

  static async revoke(id: string): Promise<DemoGenieToken> {
    const response = await ApiService.invoke<{ demoToken: DemoGenieToken }>({
      resource: "demo-genie-tokens",
      action: "revoke",
      id,
    });
    return response.demoToken;
  }

  static async list(agentId?: string): Promise<DemoGenieToken[]> {
    const response = await ApiService.invoke<{ items: DemoGenieToken[] }>({
      resource: "demo-genie-tokens",
      action: "list",
      data: agentId ? { agentId } : {},
    });
    return response?.items || [];
  }
}
